'use client';

/**
 * Aggregated on-chain stats for the lobby header — drives the "Tiles"
 * pill and the Claim/Rewards pill.
 *
 * Lobby is V2-only by product direction: V1 boards live on the classic
 * app (linked from inside the Tile Management modal). The chip total
 * therefore excludes V1 pending ETH so it matches the per-row sum the
 * RewardsSheet renders — see `claimableUsd` doc + the in-body comment
 * in the memo for the full rationale.
 *
 * Data sources are the *unauthenticated* `queryKeys.portfolio(address)`
 * and `queryKeys.pendingFees(address)` caches — same keys
 * `useLobbySeatHoldings` (manage modal / mobile rail) and
 * `useJoinedBoards` (rail / desktop list) already share. The chip used
 * to read from `useMeSummary()` (a single auth-gated `/api/me/summary`
 * round-trip), which made it silently flip to "$0.00" on any
 * deployment where the route was missing or `requireAuth` rejected
 * the wagmi address — see the in-hook comment on the queries below
 * for the full incident rationale.
 */

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTakeoverPrice } from '@/hooks/use-takeover-price';
import { useWallet } from '@/hooks/use-wallet';
import {
  getTaxTokenConfig,
  mergePortfolioPreservingCachedSeats,
} from '@/lib/drawer-utils';
import { queryKeys } from '@/lib/query-keys';
import { fetchPortfolioJson } from '@/lib/fetchers/portfolio-json';
import {
  fetchPendingFeesCached,
  type PendingFeesCachedShape,
} from '@/lib/fetchers/pending-fees-query';
import type { CreatorTaxBreakdown } from '@/lib/cache/rpc-cache';
import { CONSTANTS } from '@/lib/types';
import { resolveBalanceAndRunway, DEFAULT_TAX_RATE_BPS } from '@/lib/tax';
import { safeBigInt } from '@/lib/bigint';

export interface LobbyClaimableGrid {
  gridId: string;
  gridName: string;
  usdValue: number;
  /** Seat trading yield (tile_yield); kept for callers that key off symbol type. */
  yieldTokenSymbol?: string;
  gridImageUrl?: string | null;
  claimKind: 'tile_yield' | 'board_tax';
  /** Yield or tax token label for UI. */
  tokenSymbol?: string;
}

export interface LobbyHeaderStats {
  isLive: boolean;
  isLoading: boolean;
  tileValueUsd: number;
  seatCount: number;
  /** Total claimable USD — V2-only: sums tile yield (per-grid seat fees)
   *  and creator tax. V1 pending ETH is intentionally NOT included; V1
   *  fees live on the classic app and the lobby RewardsSheet does not
   *  render V1 rows, so including V1 here would make the chip total
   *  diverge from the sheet's per-row sum. */
  claimableUsd: number;
  claimableCount: number;
  hasAnyClaimable: boolean;
  claimableGrids: LobbyClaimableGrid[];
  /** Full pending-fees payload for onchain claim builders. */
  pendingFees: PendingFeesCachedShape | null;
  tileAlertRisk: 'safe' | 'warn' | 'danger';
  tileAlertCount: number;
  tileAlertMinRunwayDays: number;
  /** True iff at least one TAKEOVER-priced seat was excluded from
   *  `tileValueUsd` because the live TAKEOVER price oracle hasn't
   *  resolved yet. Consumers should treat the chip as loading rather
   *  than rendering the partial total — the previous fallback added
   *  the raw 18-decimal TAKEOVER bigint into the 6-decimal USD
   *  accumulator and inflated the chip by ~10¹². */
  tileValuePending: boolean;
}

const EMPTY: LobbyHeaderStats = {
  isLive: false,
  isLoading: false,
  tileValueUsd: 0,
  seatCount: 0,
  claimableUsd: 0,
  claimableCount: 0,
  hasAnyClaimable: false,
  claimableGrids: [],
  pendingFees: null,
  tileAlertRisk: 'safe',
  tileAlertCount: 0,
  tileAlertMinRunwayDays: 0,
  tileValuePending: false,
};

interface PortfolioSeat {
  isActive?: boolean;
  selfAssessedPrice?: string;
  /** Raw deposit balance (USDC 6dp for V1 / TAKEOVER 18dp for V2 grids). */
  balance?: string;
  /** Unix seconds of the last on-chain balance change (drives tax accrual). */
  lastUpdateTime?: number;
  /**
   * RPC-derived metrics. For V2 grid seats these arrive as a placeholder
   * (`currentBalance: "0"`, `timeUntilForfeiture: 0`) — `resolveBalanceAndRunway`
   * detects that and falls back to client-side computation.
   */
  metrics?: { timeUntilForfeiture?: number; currentBalance?: string };
  pool?: {
    isV2Grid?: boolean;
    taxToken?: string;
    taxTokenDecimals?: number;
    taxRateBps?: number;
    /** Lowercased grid creator address. Drives the primary-mint creator
     *  seat filter — see `isPrimaryMintCreatorSeat` below for the
     *  shared three-rule invariant (mirrored across
     *  `use-lobby-seat-holdings.ts` and `use-grid-page.helpers.ts`). */
    creatorAddress?: string;
    /** True once the board has graduated. Post-graduation creator seats
     *  lose their tax exemption (they pay like any other holder), so
     *  the chip MUST stop filtering them out at that point. */
    isGraduated?: boolean;
  };
}

interface PortfolioResponse {
  data?: {
    seats?: PortfolioSeat[];
  };
}

/**
 * Primary-mint creator seat predicate — the canonical filter that every
 * "Your Tiles" surface in the lobby applies. When the viewer is the
 * grid's creator, `getGridSeatsByHolder` returns every unsold seat they
 * hold for that grid. Those seats carry zero deposit (creators are
 * exempt from Harberger tax on their own primary-mint inventory) so the
 * forfeit-runway projection treats them as zombies and the chip would
 * surface a red "will forfeit" warning for tiles that genuinely can't
 * be poked. Post-graduation creator seats lose their exemption (they
 * pay tax like everyone else), so we only filter pre-graduation.
 *
 * The same three-rule predicate is implemented in
 * `use-lobby-seat-holdings.ts` (the manage-modal source) and
 * `use-grid-page.helpers.ts` (the per-board seat builder). Keep them
 * aligned — a divergence here surfaces as the chip disagreeing with
 * the modal/board on whether a creator seat is "yours".
 */
export function isPrimaryMintCreatorSeat(
  seat: PortfolioSeat,
  viewerLower: string | null,
): boolean {
  if (!viewerLower) return false;
  const creator = seat.pool?.creatorAddress?.toLowerCase();
  if (!creator || creator !== viewerLower) return false;
  if (seat.pool?.isGraduated) return false;
  return safeBigInt(seat.balance) === 0n;
}

function sumCreatorTaxUsd(rows: CreatorTaxBreakdown[]): number {
  return rows.reduce((s, t) => s + (t.usdValue ?? 0), 0);
}

export interface TileValueResult {
  tileValueUsd: number;
  seatCount: number;
  /** True iff at least one TAKEOVER-priced seat was excluded because the
   *  oracle hadn't ticked yet. Consumers should treat this as a loading
   *  state, not a partial total — see `LobbyHeaderStats.tileValuePending`. */
  tileValuePending: boolean;
}

/**
 * Compute the V2-only tile-value sum the header chip renders. Pure and
 * stateless so we can pin the creator-seat filter, the V2-only guard,
 * and the TAKEOVER ↔ USD conversion against representative seat shapes
 * in unit tests — the in-hook `useMemo` body is too big to test in
 * isolation otherwise.
 */
export function computeTileValueUsd(
  seats: PortfolioSeat[],
  viewerLower: string | null,
  takeoverPriceUsd: number,
): TileValueResult {
  let tileValueUsd6 = 0n;
  let seatCount = 0;
  let tileValuePending = false;
  for (const seat of seats) {
    if (seat.isActive === false) continue;
    // Lobby is V2-only — same rationale as the `computeTileAlert`
    // guard above. Including V1 prices here made the chip's dollar
    // amount and `seatCount` diverge from the V2-only sums rendered
    // by `TileManagementModal` (`v2Holdings.tileValue`).
    if (!seat.pool?.isV2Grid) continue;
    // Primary-mint creator seats are unsold inventory, not "yours" in
    // the portfolio sense — adding their `selfAssessedPrice` to the
    // chip total or counting them in `seatCount` made the header
    // diverge from `useLobbySeatHoldings` (the manage modal source),
    // which already filters them with the same predicate.
    if (isPrimaryMintCreatorSeat(seat, viewerLower)) continue;
    seatCount += 1;
    const price = safeBigInt(seat.selfAssessedPrice);
    if (price === 0n) continue;
    const ttc = getTaxTokenConfig(
      seat.pool?.taxToken,
      undefined,
      seat.pool?.taxTokenDecimals,
    );
    if (ttc.isTakeover) {
      // TAKEOVER seats need the live USD oracle to convert their
      // 18-decimal self-assessed price into a USD-6dp delta. While
      // the oracle is still warming up (`takeoverPriceUsd <= 0`)
      // we MUST NOT fall back to adding `price` directly — `price`
      // is in raw 18-dp wei and the accumulator is in USD-6dp, so
      // the addition silently inflates the chip by ~10¹² and the
      // user sees an absurd dollar figure. Skip the seat instead
      // and surface a `tileValuePending` flag so the chip can
      // render its loading skeleton until the price ticks in.
      if (takeoverPriceUsd <= 0) {
        tileValuePending = true;
        continue;
      }
      const usd6 = safeBigInt(
        Math.floor((Number(price) / 10 ** ttc.decimals) * takeoverPriceUsd * 1e6),
      );
      tileValueUsd6 += usd6;
    } else {
      // USDC (and any other 6-decimal stable) — `price` is already
      // in the same USD-6dp space as the accumulator, so direct
      // addition is correct.
      tileValueUsd6 += price;
    }
  }
  return {
    tileValueUsd: Number(tileValueUsd6) / 1e6,
    seatCount,
    tileValuePending,
  };
}

/**
 * Header-chip danger threshold. Deliberately stricter than the global
 * `CONSTANTS.RUNWAY_CRITICAL` (2 days) — the chip lights up red and pulses,
 * which is too loud for a tile that still has 36-48h of runway. Reserve red
 * for the genuinely-imminent forfeit window (<24h) where the user actually
 * needs to act today; everything between 1d and `RUNWAY_HEALTHY` (4d) still
 * surfaces as orange via the `warn` tier so users see the approach without
 * the chip being permanently stuck in alarm mode.
 */
const HEADER_DANGER_SECONDS = 24 * 60 * 60;

export function computeTileAlert(
  seats: PortfolioSeat[],
  viewerLower: string | null,
): {
  risk: 'safe' | 'warn' | 'danger';
  count: number;
  minRunwayDays: number;
} {
  let minTf = Infinity;
  let dangerCount = 0;
  let warnCount = 0;
  for (const seat of seats) {
    if (seat.isActive === false) continue;

    // Lobby is V2-only (see file docstring + `claimableUsd` rationale
    // below). V1 seats live on the classic app and are filtered out
    // of every visible surface in the lobby — `TileManagementModal`
    // does `holdings.filter(h => h.isV2)`, `OwnedTilesBar` only ever
    // renders the active V2 grid's seats. V1 metrics also aren't
    // enriched by `addRpcBalancesToV2Seats`, so a legacy V1 manager
    // that's no longer answering RPC leaves the seat with the
    // `defaultMetrics` placeholder, which routes
    // `resolveBalanceAndRunway` into client-side decay against a
    // months-old indexer `lastUpdateTime` and drives the chip orange
    // for seats the user can't even see in the lobby.
    if (!seat.pool?.isV2Grid) continue;

    // Primary-mint creator seats have `balance == 0n` and a non-zero
    // `selfAssessedPrice`, so `resolveBalanceAndRunway` returns 0s and
    // the chip would flip red on every brand-new board the viewer
    // created — even though no tax accrues and `pokeTax` cannot
    // forfeit them pre-graduation. Mirrors the filter
    // `use-lobby-seat-holdings.ts` already applies upstream so the
    // chip and the manage modal agree on what counts as "yours".
    if (isPrimaryMintCreatorSeat(seat, viewerLower)) continue;

    // V2 grid seats arrive with placeholder RPC metrics
    // (`timeUntilForfeiture === 0`, `currentBalance === "0"`) when the
    // portfolio fetcher's `addRpcBalancesToV2Seats` multicall fails;
    // route through `resolveBalanceAndRunway` — the same helper the
    // seat cards use — which detects the placeholder and falls back
    // to client-side runway from balance + price + tax rate.
    const price = (() => {
      try { return BigInt(seat.selfAssessedPrice || '0'); } catch { return 0n; }
    })();
    // Zero-price seats can never tax-out (weeklyTax = 0). Skip them so a
    // newly-claimed seat with no listed price doesn't permanently pin the
    // chip red.
    if (price === 0n) continue;

    const balance = (() => {
      try { return BigInt(seat.balance || '0'); } catch { return 0n; }
    })();
    // `safeBigInt` (not bare `BigInt`): the upstream `seat.pool` shape
    // is the portfolio JSON merged from indexer + RPC, and edge-case
    // pools have shipped `taxRateBps` as a non-integer (decimal
    // string, `NaN`, occasionally `"500.0"` from a USD-priced
    // adapter). Two of the three BigInt sites below this used to
    // crash the chip; lines 298 / 306 are already wrapped in a local
    // try/catch (returning 0n) — this one was the last unguarded
    // throw vector. Because `useLobbyHeaderStats` is consumed from
    // `LobbyNav` / `RewardsSheet` / `TileManagementModal` (all inside
    // `LobbyClient`, which mounts from `(lobby)/layout.tsx`), an
    // uncaught throw escapes `(lobby)/error.tsx` and lands at
    // `global-error.tsx` — exactly the boundary the iPhone-on-Safari
    // user sees for "every page" post-connect. Falling back to
    // `DEFAULT_TAX_RATE_BPS` keeps the runway calc on the same
    // protocol default the rest of the chip uses.
    const taxRateBps = safeBigInt(seat.pool?.taxRateBps, DEFAULT_TAX_RATE_BPS);
    const lastUpdateTime = seat.lastUpdateTime ?? 0;

    const { runwaySeconds: tf } = resolveBalanceAndRunway(
      balance,
      price,
      taxRateBps,
      lastUpdateTime,
      seat.metrics,
    );

    if (tf < minTf) minTf = tf;
    if (tf < HEADER_DANGER_SECONDS) dangerCount += 1;
    else if (tf < CONSTANTS.RUNWAY_HEALTHY) warnCount += 1;
  }
  const minDays = Number.isFinite(minTf) ? minTf / 86400 : 0;
  if (dangerCount > 0) return { risk: 'danger', count: dangerCount, minRunwayDays: minDays };
  if (warnCount > 0) return { risk: 'warn', count: warnCount, minRunwayDays: minDays };
  return { risk: 'safe', count: 0, minRunwayDays: minDays };
}

export function useLobbyHeaderStats(): LobbyHeaderStats {
  const { isConnected, address } = useWallet();
  const { priceUsd: takeoverPriceUsd } = useTakeoverPrice();
  const queryClient = useQueryClient();
  // Lowercased viewer address drives the primary-mint creator seat
  // filter — see `isPrimaryMintCreatorSeat` for the full rationale.
  // Computing it once here keeps both accumulator passes (and any
  // future callers in this hook) on a single normalized value.
  const viewerLower = address ? address.toLowerCase() : null;
  const canFetch = isConnected && !!address;

  // Why direct queries on the canonical cache keys instead of
  // `useMeSummary()`:
  //
  // The chip *used* to read its data from `useMeSummary()`, which
  // wraps a single auth-gated fetch to `/api/me/summary`. That
  // consolidation is a fine boot-storm optimisation when the route
  // is reachable, but it made the chip hostage to a single failure
  // point: any deployment where `/api/me/summary` returned a non-2xx
  // (route missing in a not-yet-redeployed prod build, or
  // `requireAuth` rejecting because the wagmi address isn't on the
  // viewer's Privy `linkedAccounts`) made `useMeSummary` silently
  // resolve to `{ portfolio: null, isLoading: false }`. The chip
  // then ran `computeTileValueUsd([], …)` and rendered "$0.00" with
  // no skeleton — even though the manage modal and mobile rail
  // (driven by `useLobbySeatHoldings`) showed the user's seats
  // correctly, because that hook reads the *unauthenticated*
  // `/api/portfolio/[address]` directly.
  //
  // Tracing wallet `0x0049…307c` confirmed this: `/api/portfolio/[addr]`
  // returned 715 seats (705 V2 → ~$3,299 of value across 7
  // USDC-priced boards + BOARDROOM); `/api/me/summary` 404'd on the
  // deployment they were on; chip rendered "$0.00".
  //
  // Reading `queryKeys.portfolio(address)` and
  // `queryKeys.pendingFees(address)` directly here aligns the chip
  // with `useLobbySeatHoldings` / `useJoinedBoards`, which already
  // share these exact keys + fetchers. TanStack Query dedupes
  // observers by `queryKey`, so when both the chip and the manage
  // modal mount on the same route the underlying `fetchPortfolioJson`
  // call still runs once. `useMeSummary` (when something else mounts
  // it on a route that wants the consolidated round-trip) still
  // hydrates the same cache via `setQueryData`, so its
  // boot-optimisation value isn't lost — the chip just no longer
  // depends on it.
  //
  // `mergePortfolioPreservingCachedSeats` keeps RPC-patched seats
  // (newer `lastPriceChangeTime`) from being clobbered by an indexer
  // refetch landing within the post-tx lag window — same reasoning
  // as the matching block in `useLobbySeatHoldings.queryFn`.
  const { data: portfolioJson, isLoading: portfolioLoading } = useQuery({
    queryKey: queryKeys.portfolio(address?.toLowerCase() ?? null),
    queryFn: async () => {
      if (!address) return {} as PortfolioResponse;
      const fresh = (await fetchPortfolioJson(address)) as PortfolioResponse | null;
      const cached = queryClient.getQueryData(
        queryKeys.portfolio(address.toLowerCase()),
      );
      const merged = mergePortfolioPreservingCachedSeats(cached, fresh);
      return (merged ?? {}) as PortfolioResponse;
    },
    enabled: canFetch,
    // Matched to `refetchInterval` — a new observer mount inside
    // the staleness window used to kick an extra fetch; consumers
    // now share the single 60s poll cadence.
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const { data: pendingFees, isLoading: feesLoading } = useQuery<
    PendingFeesCachedShape | null
  >({
    queryKey: queryKeys.pendingFees(address?.toLowerCase() ?? null),
    queryFn: async () => {
      if (!address) return null;
      try {
        return await fetchPendingFeesCached(address);
      } catch {
        // Same `null`-tolerant pattern `useLobbySeatHoldings` uses:
        // a transient pending-fees cache outage degrades the
        // Rewards chip to "no claimables" rather than tearing
        // down the chip with an `isError`.
        return null;
      }
    },
    enabled: canFetch,
    staleTime: 25_000,
  });

  const portfolio = portfolioJson as PortfolioResponse | null;

  return useMemo<LobbyHeaderStats>(() => {
    if (!isConnected) return EMPTY;
    const seats = portfolio?.data?.seats ?? [];

    const { tileValueUsd, seatCount, tileValuePending } = computeTileValueUsd(
      seats,
      viewerLower,
      takeoverPriceUsd,
    );
    const alert = computeTileAlert(seats, viewerLower);

    const pf = pendingFees ?? null;
    const breakdown = pf?.v2Breakdown ?? [];
    const claimableGrids: LobbyClaimableGrid[] = [];
    let claimableCount = 0;

    for (const g of breakdown) {
      const raw = g.rawPendingFees ?? g.pendingFees ?? '0';
      let rawBn = 0n;
      try {
        rawBn = BigInt(raw);
      } catch {
        rawBn = 0n;
      }
      if (rawBn <= 0n) continue;
      claimableCount += 1;
      const usd =
        typeof g.usdValue === 'number' && g.usdValue > 0 ? g.usdValue : 0;
      claimableGrids.push({
        gridId: g.gridId ?? '',
        gridName: g.gridName ?? (g.gridId ? `Grid ${g.gridId.split('-').pop()}` : 'Grid'),
        usdValue: usd,
        yieldTokenSymbol: g.yieldTokenSymbol,
        gridImageUrl: g.gridImageUrl ?? null,
        claimKind: 'tile_yield',
        tokenSymbol: g.yieldTokenSymbol,
      });
    }

    for (const t of pf?.creatorTaxBreakdown ?? []) {
      let hasTax = false;
      try {
        hasTax = BigInt(t.taxRevenue || '0') > 0n;
      } catch {
        hasTax = false;
      }
      if (!hasTax) continue;
      claimableCount += 1;
      claimableGrids.push({
        gridId: t.gridId,
        gridName: t.gridName ?? (t.gridId ? `Grid ${t.gridId.split('-').pop()}` : 'Grid'),
        usdValue: t.usdValue ?? 0,
        gridImageUrl: t.gridImageUrl ?? null,
        claimKind: 'board_tax',
        tokenSymbol: t.taxTokenSymbol,
      });
    }

    claimableGrids.sort((a, b) => b.usdValue - a.usdValue);

    // Header pill is V2-only: tile yield + creator tax. V1 pending ETH
    // is intentionally excluded — those funds live on the classic app
    // (linked from inside the Tile Management modal) and the lobby
    // RewardsSheet doesn't render V1 rows either, so including V1 in
    // the chip total made the pill say "$X" while the sheet's "Total
    // claimable" said "$X − v1Usd", and the user had no way to claim
    // the missing delta from the lobby.
    //
    // We deliberately don't trust the server's `pf.aggregateUsd` for
    // the chip total: that field includes V1 (see `getUserPendingFees`
    // in `lib/cache/rpc-cache.ts`). Recomputing locally from
    // `v2Breakdown` + `creatorTaxBreakdown` keeps the chip aligned with
    // `claimableGrids` (which is what the RewardsSheet renders).
    const creatorTaxTotalUsd = pf ? sumCreatorTaxUsd(pf.creatorTaxBreakdown) : 0;

    let displayUsd = 0;
    for (const g of breakdown) {
      if (safeBigInt(g.rawPendingFees ?? g.pendingFees) <= 0n) continue;
      if (typeof g.usdValue === 'number' && g.usdValue > 0) displayUsd += g.usdValue;
    }
    displayUsd += creatorTaxTotalUsd;

    const hasV2Pending = breakdown.some(
      (b) => safeBigInt(b.rawPendingFees ?? b.pendingFees) > 0n,
    );
    const hasAnyClaimable = hasV2Pending || creatorTaxTotalUsd > 0;

    return {
      isLive: true,
      isLoading: portfolioLoading || feesLoading,
      tileValueUsd,
      seatCount,
      claimableUsd: displayUsd,
      claimableCount,
      hasAnyClaimable,
      claimableGrids,
      pendingFees: pf ?? null,
      tileAlertRisk: alert.risk,
      tileAlertCount: alert.count,
      tileAlertMinRunwayDays: alert.minRunwayDays,
      tileValuePending,
    };
  }, [
    isConnected,
    portfolio,
    portfolioLoading,
    pendingFees,
    feesLoading,
    takeoverPriceUsd,
    viewerLower,
  ]);
}
